/**
 * Acquisition & receivables (Analytics spec §3.10 channel quality, §3.1 AR aging).
 * Members by acquisition source weighted by active retention (so channel quality,
 * not just volume, is visible) + accounts-receivable aging buckets. Reads 0147
 * views. Owner/manager. Backend data, read-only.
 */
import { getAcquisitionChannel, getArAging } from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedBarChart } from '../../components/charts/index.js';
import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { KpiCard } from '../../components/ui/KpiCard.js';
import { ChartCard } from '../../components/ui/Layout.js';
import { useFormatters, useLocale } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

export function AcquisitionAnalytics(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const locale = useLocale();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const on = { enabled: !!tenantId };

  const channel = useQuery({
    queryKey: ['an_channel', tenantId],
    queryFn: () => getAcquisitionChannel(supabase, tenantId),
    ...on,
  });
  const ar = useQuery({
    queryKey: ['an_ar', tenantId],
    queryFn: () => getArAging(supabase, tenantId),
    ...on,
  });

  const bars = useMemo(
    () =>
      (channel.data ?? []).map((r, i) => ({
        label: t(`members.acquisitionSource.${r.source}`, { defaultValue: r.source }),
        value: r.members,
        color: ['#22C55E', '#38BDF8', '#F59E0B', '#A78BFA', '#94A3B8'][i % 5] ?? '#94A3B8',
      })),
    [channel.data, t],
  );

  return (
    <div className="grid gap-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('analytics.intel.ar0_7')}
          value={fmt.number(ar.data?.bucket_0_7 ?? 0)}
          tone="info"
          loading={ar.isLoading}
        />
        <KpiCard
          label={t('analytics.intel.ar8_30')}
          value={fmt.number(ar.data?.bucket_8_30 ?? 0)}
          tone="warning"
          loading={ar.isLoading}
        />
        <KpiCard
          label={t('analytics.intel.ar30')}
          value={fmt.number(ar.data?.bucket_30_plus ?? 0)}
          tone="danger"
          loading={ar.isLoading}
        />
        <KpiCard
          label={t('analytics.intel.arTotal')}
          value={fmt.number(ar.data?.overdue_total ?? 0)}
          loading={ar.isLoading}
          hint={t('analytics.intel.arHint')}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard
          title={t('analytics.intel.acqByChannel')}
          subtitle={t('analytics.intel.acqSubtitle')}
          loading={channel.isLoading}
          empty={bars.length === 0 ? t('common.noData') : undefined}
        >
          <RankedBarChart
            data={bars}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>
        <ExpandableCard
          title={t('analytics.intel.acqQuality')}
          subtitle={t('analytics.intel.acqQualitySubtitle')}
          loading={channel.isLoading}
          empty={(channel.data ?? []).length === 0 ? t('common.noData') : undefined}
        >
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('members.fields.acquisitionSource')}</TH>
                  <TH numeric>{t('analytics.intel.members')}</TH>
                  <TH numeric>{t('analytics.intel.activeBase')}</TH>
                  <TH numeric>{t('analytics.intel.activePct')}</TH>
                </tr>
              </THead>
              <tbody>
                {(channel.data ?? []).map((r) => (
                  <TR key={r.source}>
                    <TD className="font-medium">
                      {t(`members.acquisitionSource.${r.source}`, { defaultValue: r.source })}
                    </TD>
                    <TD numeric>{fmt.number(r.members)}</TD>
                    <TD numeric className="text-muted">
                      {fmt.number(r.active_members)}
                    </TD>
                    <TD numeric>
                      <Chip
                        tone={
                          (r.active_pct ?? 0) >= 50
                            ? 'success'
                            : (r.active_pct ?? 0) >= 25
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {r.active_pct ?? 0}%
                      </Chip>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </ExpandableCard>
      </div>
    </div>
  );
}
